import { useState, useMemo } from 'react'
import { ArrowUp, ArrowDown, Search, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import { MetricTooltip } from './MetricTooltip'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PerfPage {
  id: number
  url: string
  pageType: string
  perfScoreMobile: number | null
  perfScoreDesktop: number | null
  seoScore: number | null
  bpScore: number | null
  lcpMs: number | null
  fcpMs: number | null
  clsValue: number | null
  ttfbMs: number | null
  inpMs: number | null
}

export interface PrevPage {
  url: string
  perfScoreMobile: number | null
  perfScoreDesktop: number | null
  lcpMs: number | null
  fcpMs: number | null
  clsValue: number | null
  ttfbMs: number | null
  inpMs: number | null
}

export interface PagesTableProps {
  pages: PerfPage[]
  projectId: string
  auditId: number
  previousPages?: PrevPage[] | null
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_SIZE = 50

const PAGE_TYPES = [
  'Tous les types',
  'homepage',
  'blog',
  'product',
  'category',
  'static',
  'other',
]

// ---------------------------------------------------------------------------
// Color helpers
// ---------------------------------------------------------------------------

const COLOR_GOOD   = '#10B981'
const COLOR_MEDIUM = '#F59E0B'
const COLOR_POOR   = '#EF4444'
const COLOR_NULL   = '#8B8FA7'

function scoreColor(value: number | null): string {
  if (value === null) return COLOR_NULL
  if (value >= 90) return COLOR_GOOD
  if (value >= 50) return COLOR_MEDIUM
  return COLOR_POOR
}

function lcpColor(ms: number | null): string {
  if (ms === null) return COLOR_NULL
  if (ms < 2500) return COLOR_GOOD
  if (ms < 4000) return COLOR_MEDIUM
  return COLOR_POOR
}

function clsColor(value: number | null): string {
  if (value === null) return COLOR_NULL
  if (value < 0.1)  return COLOR_GOOD
  if (value < 0.25) return COLOR_MEDIUM
  return COLOR_POOR
}

function inpColor(ms: number | null): string {
  if (ms === null) return COLOR_NULL
  if (ms < 200) return COLOR_GOOD
  if (ms < 500) return COLOR_MEDIUM
  return COLOR_POOR
}

// ---------------------------------------------------------------------------
// Format helpers
// ---------------------------------------------------------------------------

function fmtScore(value: number | null): string {
  return value === null ? '—' : String(value)
}

function fmtLcp(ms: number | null): string {
  if (ms === null) return '—'
  const s = ms / 1000
  return s % 1 === 0 ? `${s}s` : `${s.toFixed(1)}s`
}

function fmtCls(value: number | null): string {
  return value === null ? '—' : value.toFixed(2)
}

function fmtInp(ms: number | null): string {
  return ms === null ? '—' : `${Math.round(ms)}ms`
}

function truncateUrl(url: string, max = 60): string {
  return url.length > max ? url.slice(0, max) + '…' : url
}

// ---------------------------------------------------------------------------
// Sort helper
// ---------------------------------------------------------------------------

type SortKey = 'url' | 'pageType' | 'perfScoreMobile' | 'perfScoreDesktop' | 'lcpMs' | 'clsValue' | 'inpMs'
type SortDir = 'asc' | 'desc'

function getValue(page: PerfPage, key: SortKey): string | number {
  const v = page[key]
  if (v === null || v === undefined) return key === 'url' || key === 'pageType' ? '' : Infinity
  return v as string | number
}

function sortPages(pages: PerfPage[], key: SortKey, dir: SortDir): PerfPage[] {
  return [...pages].sort((a, b) => {
    const va = getValue(a, key)
    const vb = getValue(b, key)
    if (va === vb) return 0
    const cmp = va < vb ? -1 : 1
    return dir === 'asc' ? cmp : -cmp
  })
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface ThProps {
  label: string
  sortKey: SortKey
  currentKey: SortKey
  currentDir: SortDir
  onSort: (key: SortKey) => void
  tooltip?: 'LCP' | 'CLS' | 'INP'
  className?: string
}

function Th({ label, sortKey, currentKey, currentDir, onSort, tooltip, className = '' }: ThProps) {
  const active = currentKey === sortKey
  return (
    <th
      className={`px-3 py-2 text-left text-[12px] uppercase tracking-wide text-[#8B8FA7] font-medium whitespace-nowrap cursor-pointer select-none hover:text-[#6B6F87] ${className}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {tooltip && <MetricTooltip metric={tooltip} />}
        {active ? (
          currentDir === 'asc'
            ? <ArrowUp size={12} className="text-[#6B6F87]" />
            : <ArrowDown size={12} className="text-[#6B6F87]" />
        ) : (
          <ArrowUp size={12} className="opacity-0 group-hover:opacity-40" />
        )}
      </span>
    </th>
  )
}

const LOWER_IS_BETTER = new Set(['lcpMs', 'fcpMs', 'clsValue', 'ttfbMs', 'inpMs'])

function DeltaIndicator({ current, previous, field }: {
  current: number | null
  previous: number | null
  field: string
}) {
  if (current === null || previous === null) return null
  const diff = current - previous
  if (diff === 0) return null

  const lowerBetter = LOWER_IS_BETTER.has(field)
  const isImprovement = lowerBetter ? diff < 0 : diff > 0

  return (
    <span
      className="inline-flex items-center ml-1 text-[10px] font-medium"
      style={{ color: isImprovement ? COLOR_GOOD : COLOR_POOR }}
    >
      {isImprovement ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
    </span>
  )
}

interface ColoredCellProps {
  display: string
  color: string
  isNull: boolean
  children?: React.ReactNode
}

function ColoredCell({ display, color, isNull, children }: ColoredCellProps) {
  return (
    <td
      className="px-3 py-2 text-[13px] font-medium tabular-nums"
      style={{ color: isNull ? COLOR_NULL : color }}
    >
      {display}
      {children}
    </td>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function PagesTable({ pages, projectId, previousPages }: PagesTableProps) {
  const [urlFilter, setUrlFilter]   = useState('')
  const [typeFilter, setTypeFilter] = useState('Tous les types')
  const [sortKey, setSortKey]       = useState<SortKey>('perfScoreMobile')
  const [sortDir, setSortDir]       = useState<SortDir>('asc')
  const [page, setPage]             = useState(1)

  const prevPageMap = useMemo(() => {
    if (!previousPages) return null
    const map = new Map<string, PrevPage>()
    for (const p of previousPages) map.set(p.url, p)
    return map
  }, [previousPages])

  // Filter
  const filtered = useMemo(() => {
    return pages.filter((p) => {
      const matchUrl  = p.url.toLowerCase().includes(urlFilter.toLowerCase())
      const matchType = typeFilter === 'Tous les types' || p.pageType === typeFilter
      return matchUrl && matchType
    })
  }, [pages, urlFilter, typeFilter])

  // Sort
  const sorted = useMemo(() => sortPages(filtered, sortKey, sortDir), [filtered, sortKey, sortDir])

  // Pagination
  const totalPages    = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const currentPage   = Math.min(page, totalPages)
  const pageStart     = (currentPage - 1) * PAGE_SIZE
  const pageItems     = sorted.slice(pageStart, pageStart + PAGE_SIZE)

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
    setPage(1)
  }

  function handleUrlFilter(v: string) {
    setUrlFilter(v)
    setPage(1)
  }

  function handleTypeFilter(v: string) {
    setTypeFilter(v)
    setPage(1)
  }

  const sortProps = { currentKey: sortKey, currentDir: sortDir, onSort: handleSort }

  return (
    <div className="flex flex-col gap-3">
      {/* Filters */}
      <div className="flex items-center gap-2">
        {/* URL search */}
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8B8FA7] pointer-events-none" />
          <input
            type="text"
            value={urlFilter}
            onChange={(e) => handleUrlFilter(e.target.value)}
            placeholder="Filtrer par URL..."
            className="w-full pl-8 pr-3 py-1.5 text-[13px] bg-white border border-gray-200 rounded-md outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 placeholder:text-[#8B8FA7]"
          />
        </div>

        {/* Type filter */}
        <select
          value={typeFilter}
          onChange={(e) => handleTypeFilter(e.target.value)}
          className="py-1.5 px-2 text-[13px] bg-white border border-gray-200 rounded-md outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 text-gray-700 cursor-pointer"
        >
          {PAGE_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>

        {/* Result count */}
        <span className="text-[12px] text-[#8B8FA7] ml-1">
          {sorted.length} résultat{sorted.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="w-full overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#FAFBFD] border-b border-gray-200">
              <Th label="URL"          sortKey="url"             {...sortProps} className="min-w-[200px]" />
              <Th label="Type"         sortKey="pageType"        {...sortProps} />
              <Th label="Perf Mobile"  sortKey="perfScoreMobile" {...sortProps} />
              <Th label="Perf Desktop" sortKey="perfScoreDesktop" {...sortProps} />
              <Th label="LCP"          sortKey="lcpMs"           {...sortProps} tooltip="LCP" />
              <Th label="CLS"          sortKey="clsValue"        {...sortProps} tooltip="CLS" />
              <Th label="INP"          sortKey="inpMs"           {...sortProps} tooltip="INP" />
              <th className="px-3 py-2 text-left text-[12px] uppercase tracking-wide text-[#8B8FA7] font-medium w-10" />
            </tr>
          </thead>

          <tbody>
            {pageItems.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-[13px] text-[#8B8FA7]">
                  Aucune page trouvée
                </td>
              </tr>
            ) : (
              pageItems.map((p, i) => {
                const isEven = i % 2 === 0
                return (
                  <tr
                    key={p.id}
                    className={`h-9 border-b border-gray-100 last:border-0 transition-colors hover:bg-light-surface-hover ${isEven ? 'bg-white' : 'bg-[#FAFBFD]'}`}
                  >
                    {/* URL */}
                    <td className="px-3 py-2 text-[13px] text-gray-700 max-w-[300px]">
                      <span title={p.url} className="block truncate">
                        {truncateUrl(p.url)}
                      </span>
                    </td>

                    {/* Type */}
                    <td className="px-3 py-2">
                      <span className="inline-block text-[11px] px-1.5 py-0.5 rounded bg-light-elevated text-[#8B8FA7] font-medium whitespace-nowrap">
                        {p.pageType || '—'}
                      </span>
                    </td>

                    {/* Perf Mobile */}
                    <ColoredCell
                      display={fmtScore(p.perfScoreMobile)}
                      color={scoreColor(p.perfScoreMobile)}
                      isNull={p.perfScoreMobile === null}
                    >
                      {prevPageMap && (
                        <DeltaIndicator
                          current={p.perfScoreMobile}
                          previous={prevPageMap.get(p.url)?.perfScoreMobile ?? null}
                          field="perfScoreMobile"
                        />
                      )}
                    </ColoredCell>

                    {/* Perf Desktop */}
                    <ColoredCell
                      display={fmtScore(p.perfScoreDesktop)}
                      color={scoreColor(p.perfScoreDesktop)}
                      isNull={p.perfScoreDesktop === null}
                    >
                      {prevPageMap && (
                        <DeltaIndicator
                          current={p.perfScoreDesktop}
                          previous={prevPageMap.get(p.url)?.perfScoreDesktop ?? null}
                          field="perfScoreDesktop"
                        />
                      )}
                    </ColoredCell>

                    {/* LCP */}
                    <ColoredCell
                      display={fmtLcp(p.lcpMs)}
                      color={lcpColor(p.lcpMs)}
                      isNull={p.lcpMs === null}
                    >
                      {prevPageMap && (
                        <DeltaIndicator
                          current={p.lcpMs}
                          previous={prevPageMap.get(p.url)?.lcpMs ?? null}
                          field="lcpMs"
                        />
                      )}
                    </ColoredCell>

                    {/* CLS */}
                    <ColoredCell
                      display={fmtCls(p.clsValue)}
                      color={clsColor(p.clsValue)}
                      isNull={p.clsValue === null}
                    >
                      {prevPageMap && (
                        <DeltaIndicator
                          current={p.clsValue}
                          previous={prevPageMap.get(p.url)?.clsValue ?? null}
                          field="clsValue"
                        />
                      )}
                    </ColoredCell>

                    {/* INP */}
                    <ColoredCell
                      display={fmtInp(p.inpMs)}
                      color={inpColor(p.inpMs)}
                      isNull={p.inpMs === null}
                    >
                      {prevPageMap && (
                        <DeltaIndicator
                          current={p.inpMs}
                          previous={prevPageMap.get(p.url)?.inpMs ?? null}
                          field="inpMs"
                        />
                      )}
                    </ColoredCell>

                    {/* Actions */}
                    <td className="px-3 py-2">
                      <Link
                        to={`/p/${projectId}/performance/page/${p.id}`}
                        className="inline-flex items-center justify-center text-[#8B8FA7] hover:text-indigo-500 transition-colors"
                        title="Voir le détail"
                      >
                        <ExternalLink size={14} />
                      </Link>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-1">
          <span className="text-[12px] text-[#8B8FA7]">
            Page {currentPage} sur {totalPages}
          </span>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-[13px] border border-gray-200 rounded-md bg-white hover:bg-[#FAFBFD] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Précédent
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-[13px] border border-gray-200 rounded-md bg-white hover:bg-[#FAFBFD] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default PagesTable
